import { Injector } from '@angular/core';
import { map, merge } from 'rxjs';
import { KeyboardShortcutService } from '../../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import {
  Point2D,
  Point3D,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import {
  addPoints,
  dotProduct,
  subtractPoints,
} from '../../../utils/point-utils';
import { Axis } from '../edit-panel.component';
import {
  AbstractEditPanelDragHandler,
  EditPanelMouseState,
} from './abstract-edit-panel-drag-handler';
import { isPointInsideBox, NdcBoundingBox } from './drag-handler-utils';

export class TranslationDragHandler extends AbstractEditPanelDragHandler {
  private readonly keyboardShortcutService: KeyboardShortcutService;

  private lastMouseState: EditPanelMouseState;
  private shouldLockAxis = false;
  private panelAxes: [Axis, Axis];

  constructor(
    axis: Axis,
    panelDirections: Point2D,
    initialEvent: MouseEvent,
    injector: Injector,
  ) {
    super(axis, injector, initialEvent);

    this.keyboardShortcutService = injector.get(KeyboardShortcutService);

    this.lastMouseState = this.initialMouseState;
    this.shouldLockAxis = initialEvent.altKey;
    this.panelAxes = [
      Math.abs(panelDirections[0]),
      Math.abs(panelDirections[1]),
    ] as [Axis, Axis];

    this.subscribeToLockAxis();
  }

  protected override onDrag(currentMouseState: EditPanelMouseState) {
    let translation: Point3D;
    if (this.shouldLockAxis) {
      translation = this.getLockedTranslation(currentMouseState.container);
    } else {
      translation = subtractPoints(
        currentMouseState.world,
        this.initialMouseState.world,
      );
    }

    const newPosition = addPoints(this.initialLocation.position, translation);

    this.store.getFocusedCuboidLocationStore()('position').set(newPosition);
    this.lastMouseState = currentMouseState;
  }

  protected override cleanUp() {
    this.store('editPanel')('highlightedTranslationAxis').delete();
  }

  protected override getTrackEvent() {
    return {
      method: 'translate',
      sub_method: this.shouldLockAxis
        ? 'locked_translation'
        : 'free_translation',
    };
  }

  private getLockedTranslation(containerPoint: Point2D) {
    const delta = subtractPoints(
      containerPoint,
      this.initialMouseState.container,
    );
    const horizontalAxis: Point2D = [1, 0];
    const verticalAxis: Point2D = [0, 1];

    const horizontalMagnitude = Math.abs(dotProduct(delta, horizontalAxis));
    const verticalMagnitude = Math.abs(dotProduct(delta, verticalAxis));
    const directionToClear = horizontalMagnitude > verticalMagnitude ? 1 : 0;

    const lockedDirection = (directionToClear + 1) % 2;
    const highlightedAxis = this.panelAxes[lockedDirection];
    this.store('editPanel')('highlightedTranslationAxis').set(highlightedAxis);

    const newContainerPoint: Point2D = [...containerPoint];
    newContainerPoint[directionToClear] =
      this.initialMouseState.container[directionToClear];

    return subtractPoints(
      this.getWorldPoint(newContainerPoint),
      this.initialMouseState.world,
    );
  }

  private subscribeToLockAxis() {
    const isAltDown$ = merge(
      this.keyboardShortcutService
        .getMatchingEvent$('altDown')
        .pipe(map(() => true)),
      this.keyboardShortcutService
        .getMatchingEvent$('altUp')
        .pipe(map(() => false)),
    );

    this.subscribeTo(isAltDown$, (isAltDown) => {
      this.shouldLockAxis = isAltDown;
      if (!this.shouldLockAxis) {
        this.cleanUp();
      }
      this.onDrag(this.lastMouseState);
    });
  }
}

export function isOverTranslate(
  containerPoint: Point2D,
  boundingBox: NdcBoundingBox,
) {
  return isPointInsideBox(containerPoint, boundingBox);
}
